import { prefetchLink } from '@utils';
import { f as fetchHTML } from './index-Da0J-i1G.js';
import { T } from './index-CqR5nPAy.js';
import './index.esm-DDSMs62-.js';

function last(arr) {
    return arr[arr.length - 1];
}

function isPlainObject(value) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    const hasObjectPrototype = proto === null ||
        proto === Object.prototype ||
        Object.getPrototypeOf(proto) === null;
    if (!hasObjectPrototype) {
        return false;
    }
    return Object.prototype.toString.call(value) === '[object Object]';
}

function isArray(value) {
    return Array.isArray(value);
}

function capitalize(str) {
    return (str.charAt(0).toUpperCase() + str.slice(1).toLowerCase());
}

const CASE_SPLIT_PATTERN = /\p{Lu}?\p{Ll}+|[0-9]+|\p{Lu}+(?!\p{Ll})|\p{Emoji_Presentation}|\p{Extended_Pictographic}|\p{L}+/gu;
function words(str) {
    return Array.from(str.match(CASE_SPLIT_PATTERN) ?? []);
}

function camelCase(str) {
    const words$1 = words(str);
    if (words$1.length === 0) {
        return '';
    }
    const [first, ...rest] = words$1;
    return `${first.toLowerCase()}${rest.map(word => capitalize(word)).join('')}`;
}

function toCamelCaseKeys(obj) {
    if (isArray(obj)) {
        return obj.map(item => toCamelCaseKeys(item));
    }
    if (isPlainObject(obj)) {
        const result = {};
        const keys = Object.keys(obj);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const camelKey = camelCase(key);
            const convertedValue = toCamelCaseKeys(obj[key]);
            result[camelKey] = convertedValue;
        }
        return result;
    }
    return obj;
}

/**
 * Custom element that implements infinite scrolling functionality
 * Observes the last item in a list and loads more content when it becomes visible
 */
class InfiniteLoad extends HTMLElement {
  paginate = toCamelCaseKeys(JSON.parse(this.dataset.paginate));
  lastPageSize = this.paginate.items % this.paginate.pageSize || this.paginate.pageSize;
  currentItems = this.paginate.pageSize;
  productList = this.querySelector('[data-products-list]');
  products = [...this.productList.querySelectorAll('& > li:not([data-promo-block])')];
  observerTarget = last(this.products);
  previousObserverTarget = this.observerTarget;
  url = new URL(window.location.href);
  liClassList = ['animate-pulse', 'bg-gray-200'];

  connectedCallback() {
    this.initIntersectionObserver();
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver(
      ([{ isIntersecting }]) => {
        if (!isIntersecting) return;
        // Unobserve the previous observer target
        this.observer.unobserve(this.observerTarget);

        // Increment the current page
        this.paginate.currentPage++;

        // If the current page is greater than the total pages, return
        if (this.paginate.currentPage > this.paginate.pages) return;

        // Create the new list items
        const listItems = this.createListItems();
        this.productList.append(...listItems);

        // Fetch the new page
        this.fetchPage();

        // Set the new observer target
        this.observerTarget = last(listItems);

        // Observe the new observer target
        this.observer.observe(this.observerTarget);
      },
      {
        root: null,
        threshold: 0,
      }
    );

    this.observer.observe(this.observerTarget);
  }

  createListItems() {
    const pageSize =
      this.paginate.currentPage === this.paginate.pages
        ? this.lastPageSize
        : this.paginate.pageSize;
    const listItems = [];
    for (let i = 0; i < pageSize; i++) {
      listItems.push(this.createListItem());
    }
    return listItems;
  }

  createListItem() {
    this.currentItems++;
    const li = document.createElement('li');
    li.id = `product-card--${this.currentItems}`;
    li.classList.add('flex');

    const img = document.createElement('div');
    img.classList.add('animate-pulse', 'bg-gray-200', 'aspect-[6/7]', 'w-full');
    li.appendChild(img);

    return li;
  }

  async fetchPage() {
    this.url.searchParams.set('page', this.paginate.currentPage);

    try {
      const html = await fetchHTML(this.url);
      this.appendNewProducts(html);
    } catch (error) {
      console.error('Failed to fetch more content:', error);
    }
  }

  /**
   * Appends new products to the list and updates the last item reference
   * @param {Document} html - The HTML document to append the new products to
   */
  appendNewProducts(html) {
    const products = html.querySelectorAll('[data-products-list] > li:not([data-promo-block])');

    products.forEach((product) => {
      const li = document.getElementById(product.id);
      li.innerHTML = product.innerHTML;
      li.classList.remove(...this.liClassList);
      li.removeAttribute('data-product-card-placeholder');

      const images = li.querySelectorAll('img');
      images.forEach((image) => {
        image.style.removeProperty('display');
        image.style.removeProperty('visibility');
      });

      const link = product.querySelectorAll('a');

      link.forEach((link) => {
        T.instance.register({
          element: link,
          name: `prefetch-${link.href}`,
          callback: () => prefetchLink(link),
        });
      });
    });
  }
}

customElements.define('infinite-load', InfiniteLoad);
